/**
 * useRunLogsLive — live-tail view of crawl_logs for a single job run.
 *
 * Pairs an initial paginated fetch with a Supabase realtime channel that
 * appends INSERT events as they arrive. Filters are applied client-side
 * on the buffered set so the user can toggle without re-fetching.
 *
 * Console-logs every state change so the Chrome DevTools MCP loop can
 * introspect what the UI is seeing while debugging.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchCrawlLogs, type CrawlLogEntry } from "@/lib/api";
import { supabase } from "@/integrations/supabase/client";

export type LogLevel = "info" | "warn" | "error" | "debug";
export type LogKind =
  | "discovery"
  | "scrape"
  | "seat"
  | "provider"
  | "trigger"
  | "checkpoint";

export interface LogFilters {
  levels?: LogLevel[];
  kinds?: LogKind[];
  adapter?: string;
  suburb?: string;
  search?: string;
}

interface UseRunLogsLiveResult {
  logs: CrawlLogEntry[];
  filtered: CrawlLogEntry[];
  isLoading: boolean;
  isLive: boolean;
  setLive: (live: boolean) => void;
  filters: LogFilters;
  setFilters: (next: LogFilters) => void;
  newSinceLastView: number;
  resetCounter: () => void;
}

const MAX_BUFFER = 2000;
const LOG_PREFIX = "[useRunLogsLive]";

export function useRunLogsLive(
  jobRunId: string | null | undefined,
  initialFilters: LogFilters = {},
): UseRunLogsLiveResult {
  const [logs, setLogs] = useState<CrawlLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLive, setLive] = useState(true);
  const [filters, setFilters] = useState<LogFilters>(initialFilters);
  const [newSinceLastView, setNewSince] = useState(0);
  const channelKeyRef = useRef<string | null>(null);

  const resetCounter = useCallback(() => setNewSince(0), []);

  // Initial paginated fetch on jobRunId change. We pull a generous tail so
  // the screen isn't empty when the user opens a run that's been running
  // for hours; further filtering happens client-side.
  useEffect(() => {
    if (!jobRunId) {
      setLogs([]);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    fetchCrawlLogs({ jobRunId, limit: 500 })
      .then(({ data }) => {
        if (cancelled) return;
        const sorted = [...data].sort((a, b) =>
          a.created_at < b.created_at ? -1 : 1,
        );
        setLogs(sorted);
        console.log(`${LOG_PREFIX} initial fetch`, {
          jobRunId,
          count: sorted.length,
        });
      })
      .catch((err) => console.error(`${LOG_PREFIX} initial fetch failed`, err))
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [jobRunId]);

  // Realtime subscription. When live is off we keep the channel up (so we
  // don't lose continuity if the user toggles back on) but stop appending.
  useEffect(() => {
    if (!jobRunId) return;
    const key = `crawl_logs:run:${jobRunId}:${Date.now()}`;
    channelKeyRef.current = key;
    const channel = supabase
      .channel(key)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "crawl_logs",
          filter: `job_run_id=eq.${jobRunId}`,
        },
        (payload) => {
          const row = payload.new as CrawlLogEntry;
          setLogs((prev) => {
            // Coalesce overflow so the buffer doesn't grow unbounded.
            const next = prev.length >= MAX_BUFFER
              ? [...prev.slice(prev.length - MAX_BUFFER + 1), row]
              : [...prev, row];
            return next;
          });
          if (!isLive) setNewSince((n) => n + 1);
        },
      )
      .subscribe((status) => {
        console.log(`${LOG_PREFIX} subscription status`, { jobRunId, status });
      });

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [jobRunId, isLive]);

  const filtered = useMemo(() => {
    return applyFilters(logs, filters);
  }, [logs, filters]);

  useEffect(() => {
    console.log(`${LOG_PREFIX} filters`, { jobRunId, ...filters, filteredCount: filtered.length });
  }, [jobRunId, filters, filtered.length]);

  return {
    logs,
    filtered,
    isLoading,
    isLive,
    setLive,
    filters,
    setFilters,
    newSinceLastView,
    resetCounter,
  };
}

function applyFilters(logs: CrawlLogEntry[], f: LogFilters): CrawlLogEntry[] {
  if (
    (!f.levels || f.levels.length === 0)
    && (!f.kinds || f.kinds.length === 0)
    && !f.adapter
    && !f.suburb
    && !f.search
  ) {
    return logs;
  }
  const searchLower = f.search?.toLowerCase();
  return logs.filter((row) => {
    if (f.levels && f.levels.length > 0 && !f.levels.includes(row.level as LogLevel)) {
      return false;
    }
    if (f.kinds && f.kinds.length > 0) {
      const k = (row.metadata as Record<string, unknown> | null)?.kind as string | undefined;
      if (!k || !f.kinds.includes(k as LogKind)) return false;
    }
    if (f.adapter) {
      const a = (row.metadata as Record<string, unknown> | null)?.adapter as string | undefined;
      if (a !== f.adapter) return false;
    }
    if (f.suburb) {
      const s = (row.metadata as Record<string, unknown> | null)?.suburb as string | undefined;
      if (s !== f.suburb) return false;
    }
    if (searchLower && !row.message.toLowerCase().includes(searchLower)) {
      return false;
    }
    return true;
  });
}
